import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Mail, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: "ad_hoc", label: "Progetto ad hoc (su misura)" },
  { value: "portafoglio", label: "Soluzione da portafoglio" },
  { value: "informazioni", label: "Informazioni generali" },
];

const initialForm = { name: "", email: "", company: "", project_type: "ad_hoc", message: "" };

const inputClass =
  "w-full rounded-lg border border-white/10 bg-obsidian/60 px-4 py-3.5 text-ink placeholder:text-zinc-600 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Inserisci il tuo nome.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    if (form.message.trim().length < 5) {
      setError("Scrivici qualche riga sul tuo progetto.");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(initialForm);
      toast.success("Richiesta inviata! Ti risponderò al più presto.", {
        duration: 5000,
      });
    } catch (err) {
      setError("Invio non riuscito. Riprova tra poco.");
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative py-24 sm:py-32">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <SectionHeading
          overline="Contatti"
          title={<>Raccontami il tuo progetto.</>}
          testId="contact-title"
        />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed text-lg max-w-md">
              Raccontami la tua esigenza: progetterò una soluzione ad hoc
              oppure ti proporrò un formato del mio portafoglio.
            </p>

            <div className="mt-10 space-y-6">
              <div data-testid="contact-address-info" className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ember/10 text-ember">
                  <MapPin className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                    Sede
                  </p>
                  <p className="text-ink">
                    Via Ferruccio Busoni 8, Milano
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ember/10 text-ember">
                  <Mail className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                    Email
                  </p>
                  <a
                    href="mailto:giango@giango.it"
                    className="text-ink hover:text-ember transition-colors duration-300"
                  >
                    giango@giango.it
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-2xl border border-white/10 bg-surface p-7 sm:p-10 space-y-6"
            >
              {sent && (
                <div
                  data-testid="contact-success-toast"
                  className="flex items-center gap-3 rounded-lg border border-mint/40 bg-mint/10 px-4 py-3.5 text-mint text-sm"
                >
                  <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                  Richiesta inviata con successo. Ti risponderò al più presto.
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cvr-name" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                    Nome *
                  </label>
                  <input
                    id="cvr-name"
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Il tuo nome"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cvr-email" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                    Email *
                  </label>
                  <input
                    id="cvr-email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="nome@azienda.it"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cvr-company" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                    Azienda
                  </label>
                  <input
                    id="cvr-company"
                    data-testid="contact-input-company"
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Nome della tua azienda"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="cvr-project-type" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                    Tipo di progetto
                  </label>
                  <select
                    id="cvr-project-type"
                    data-testid="contact-select-service"
                    value={form.project_type}
                    onChange={update("project_type")}
                    className={`cvr-select ${inputClass} pr-10`}
                  >
                    {PROJECT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="cvr-message" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                  Messaggio *
                </label>
                <textarea
                  id="cvr-message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Raccontami la tua esigenza: comunicazione interna, evento, team building, video..."
                  rows={5}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {error && (
                <p data-testid="contact-error-message" className="text-sm text-ember">
                  {error}
                </p>
              )}

              <motion.button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 bg-ember text-obsidian font-semibold px-8 py-4 rounded-full hover:bg-ember-light transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    Invio in corso
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  </>
                ) : (
                  <>
                    Invia la richiesta
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                  </>
                )}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
